import './CustomSavedFiltersDropdown.scss';

import React, { useCallback, useEffect, useMemo } from 'react';
import type { OptionProps } from 'react-select';
import { components } from 'react-select';

import Dropdown from '../../../../common/Dropdown';
import type { DropdownOption, DropdownOptionGroup, SingleSelectValue } from '../../../../common/Dropdown/Dropdown';
import { commonCustomComponents } from '../../../../common/Dropdown/Dropdown';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import bem from '../../../../utils/bem';
import CustomFilterDeleteButton from './CustomFilterDeleteButton';
import CustomFilterDeleteModal from './CustomFilterDeleteModal';
import { useCustomSavedFilters } from './hooks/useCustomSavedFilters';
import { usePendingFilterDelete } from './hooks/usePendingFilterDelete';
import type { TraffickingCustomFilterSummary } from './types';
import { CustomFilterType } from './types';

const GroupHeading = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <div style={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#646f85', padding: '4px 8px' }}>
    {children}
  </div>
);

export interface CustomSavedFiltersDropdownProps {
  variant?: 'panel' | 'toolbar';
  onFilterSummaryChange?: (summary: TraffickingCustomFilterSummary | null) => void;
}

const [block, element] = bem('custom-saved-filters-dropdown');

type FilterOptionProps = OptionProps<
  DropdownOption<TraffickingCustomFilterSummary>,
  false,
  DropdownOptionGroup<TraffickingCustomFilterSummary>
> & {
  onRequestDelete: (filter: TraffickingCustomFilterSummary) => void;
};

const FilterOption = ({ onRequestDelete, ...optionProps }: FilterOptionProps): React.JSX.Element => {
  const summary = optionProps.data.value;
  const canShowDeleteButton = !!summary?.createdByMe && summary.type !== CustomFilterType.GLOBAL;

  return (
    <components.Option {...optionProps}>
      <div className={element('option-row')}>
        <span className={element('option-label')}>{optionProps.data.label}</span>
        {canShowDeleteButton && (
          <span
            className={element('option-delete-wrapper')}
            role="presentation"
            onClick={(e): void => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <CustomFilterDeleteButton
              tooltipId={`custom-filter-delete-tooltip-${summary.id}`}
              filterName={optionProps.data.label}
              visibility={summary.visibility}
              onClick={(): void => {
                onRequestDelete(summary);
              }}
              tooltipPlace="left"
              dataTestId={`custom-filter-delete-btn-${summary.id}`}
            />
          </span>
        )}
      </div>
    </components.Option>
  );
};

const createOptionComponent = (onRequestDelete: (filter: TraffickingCustomFilterSummary) => void) => (
  optionProps: OptionProps<
    DropdownOption<TraffickingCustomFilterSummary>,
    false,
    DropdownOptionGroup<TraffickingCustomFilterSummary>
  >
): React.JSX.Element => <FilterOption {...optionProps} onRequestDelete={onRequestDelete} />;

const CustomSavedFiltersDropdown = ({
  variant = 'panel',
  onFilterSummaryChange,
}: CustomSavedFiltersDropdownProps): JSX.Element => {
  const {
    groups,
    isLoading,
    selectedFilter,
    selectedFilterSummary,
    onSelectFilter,
    onClearFilter,
  } = useCustomSavedFilters();

  const { adsMcCustomFilterDeleteEnabled } = useFeatureFlags();
  const selectedFilterId = selectedFilter?.id;

  const handleDeleted = useCallback(
    (deleted: TraffickingCustomFilterSummary): void => {
      if (selectedFilterId === deleted.id) {
        onClearFilter();
      }
    },
    [selectedFilterId, onClearFilter]
  );

  const {
    pendingTarget: pendingDeleteFilter,
    isDeleting,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = usePendingFilterDelete<TraffickingCustomFilterSummary>({ onDeleted: handleDeleted });

  useEffect(() => {
    onFilterSummaryChange?.(selectedFilterSummary);
  }, [selectedFilterSummary, onFilterSummaryChange]);

  const handleChange = useCallback(
    (option: DropdownOption<TraffickingCustomFilterSummary> | null): void => {
      if (option) {
        onSelectFilter(option);
      } else {
        onClearFilter();
      }
    },
    [onSelectFilter, onClearFilter]
  );

  const FilterOptionComponent = useMemo(() => createOptionComponent(requestDelete), [requestDelete]);

  const displayValue: SingleSelectValue<TraffickingCustomFilterSummary> = selectedFilter
    ? { id: selectedFilter.id, label: selectedFilter.label, value: (null as unknown) as TraffickingCustomFilterSummary }
    : null;

  const isEmpty = !isLoading && groups.length === 0;

  return (
    <div className={block(variant)} data-testid="custom-saved-filters-dropdown">
      {isEmpty && (
        <div className={element('empty')} data-testid="custom-saved-filters-empty">
          No saved filters
        </div>
      )}
      {!isEmpty && (
        <Dropdown<TraffickingCustomFilterSummary>
          options={groups}
          value={displayValue}
          onChange={handleChange as (value: SingleSelectValue<TraffickingCustomFilterSummary>) => void}
          isLoading={isLoading}
          emptyDisplayText={
            variant === 'toolbar' ? selectedFilter?.label ?? 'Saved Filters' : 'Select a saved filter...'
          }
          dataTestId="custom-saved-filters-select"
          components={{
            ...commonCustomComponents,
            GroupHeading,
            ...(adsMcCustomFilterDeleteEnabled && { Option: FilterOptionComponent }),
          }}
          formatGroupLabel={(group: DropdownOptionGroup<TraffickingCustomFilterSummary>): string => group.label}
        />
      )}
      {adsMcCustomFilterDeleteEnabled && (
        <CustomFilterDeleteModal
          visible={!!pendingDeleteFilter}
          filterName={pendingDeleteFilter?.name}
          loading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default CustomSavedFiltersDropdown;
